import { useEffect, useRef, useState } from 'react';
import { s, typographyTokens } from '@/theme/tokens';
import { formatPercent } from '@/ui/formatters';
import { Box, ChartSwatch, Text } from '@/ui/primitives';
import type { ReactElement } from 'react';

export type StatusPieLegendEntry = {
  id: string;
  name: string;
  value: number;
  color?: string;
};

type StatusPieLegendProps = {
  entries: StatusPieLegendEntry[];
  total: number;
};

const statusLegendValueFormatter = new Intl.NumberFormat('en-GB');
const statusLegendCountColumnWidth = 'minmax(44px, max-content)';
const statusLegendPercentColumnWidth = 'minmax(60px, max-content)';
const statusLegendGridTemplateWithPercent = [
  s('lg'),
  'minmax(0, 1fr)',
  statusLegendCountColumnWidth,
  statusLegendPercentColumnWidth,
].join(' ');
const statusLegendGridTemplateWithoutPercent = [
  s('lg'),
  'minmax(0, 1fr)',
  statusLegendCountColumnWidth,
].join(' ');
const statusLegendLabelFontSizePx = 13;
const statusLegendPercentageFontSizePx = 13;
const statusLegendLineHeight = 1.3;

export const StatusPieLegend = ({ entries, total }: StatusPieLegendProps): ReactElement => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const [hidePercentages, setHidePercentages] = useState(false);

  useEffect(() => {
    const containerElement = containerRef.current;
    const measureElement = measureRef.current;
    if (!containerElement || !measureElement || typeof ResizeObserver === 'undefined') return;

    let rafId = 0;
    const getClippingRight = (): number => {
      let clippingRight = containerElement.getBoundingClientRect().right;
      let parentElement = containerElement.parentElement;
      while (parentElement) {
        const overflowX = window.getComputedStyle(parentElement).overflowX;
        if (
          overflowX === 'hidden' ||
          overflowX === 'clip' ||
          overflowX === 'auto' ||
          overflowX === 'scroll'
        ) {
          clippingRight = Math.min(clippingRight, parentElement.getBoundingClientRect().right);
        }
        parentElement = parentElement.parentElement;
      }
      return clippingRight;
    };
    const measure = (): void => {
      const availableWidth = measureElement.clientWidth;
      const requiredWidth = measureElement.scrollWidth;
      if (!Number.isFinite(availableWidth) || !Number.isFinite(requiredWidth)) return;
      const clippingRight = getClippingRight();
      const percentageCells = Array.from(
        measureElement.querySelectorAll<HTMLElement>('[data-status-pie-legend-percent="true"]'),
      );
      const percentageOverflows = percentageCells.some(
        (cell) => cell.getBoundingClientRect().right > clippingRight - 1,
      );
      setHidePercentages(requiredWidth > availableWidth + 1 || percentageOverflows);
    };
    const schedule = (): void => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    };
    schedule();

    const observer = new ResizeObserver(() => {
      schedule();
    });
    observer.observe(containerElement);
    observer.observe(measureElement);

    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [entries, total]);

  const renderRow = (
    entry: StatusPieLegendEntry,
    index: number,
    showPercentage: boolean,
    measuring = false,
  ): ReactElement => {
    const percentage = total > 0 ? formatPercent((entry.value / total) * 100) : '0';
    const rowKey = [
      entry.id,
      String(index),
      showPercentage ? 'with-percent' : 'without-percent',
      measuring ? 'measure' : 'visible',
    ].join('-');
    return (
      <Box
        key={rowKey}
        sx={{
          display: 'grid',
          gridTemplateColumns: showPercentage
            ? statusLegendGridTemplateWithPercent
            : statusLegendGridTemplateWithoutPercent,
          alignItems: 'center',
          columnGap: s('md'),
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <ChartSwatch color={entry.color} />
        </Box>
        <Text
          role="body"
          component="span"
          sx={{
            color: 'text.secondary',
            fontSize: (muiTheme) => muiTheme.typography.pxToRem(statusLegendLabelFontSizePx),
            fontWeight: typographyTokens.body.fontWeight,
            lineHeight: statusLegendLineHeight,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {entry.name}
        </Text>
        <Text
          role="body"
          component="span"
          sx={{
            color: 'text.secondary',
            fontSize: (muiTheme) => muiTheme.typography.pxToRem(statusLegendLabelFontSizePx),
            fontVariantNumeric: 'tabular-nums',
            fontWeight: typographyTokens.bodyMedium.fontWeight,
            justifySelf: 'end',
            lineHeight: statusLegendLineHeight,
            whiteSpace: 'nowrap',
          }}
        >
          {statusLegendValueFormatter.format(entry.value)}
        </Text>
        {showPercentage ? (
          <Text
            data-status-pie-legend-percent={measuring ? 'true' : undefined}
            role="body"
            component="span"
            sx={{
              color: 'text.secondary',
              fontSize: (muiTheme) => muiTheme.typography.pxToRem(statusLegendPercentageFontSizePx),
              fontVariantNumeric: 'tabular-nums',
              fontWeight: typographyTokens.body.fontWeight,
              justifySelf: 'end',
              lineHeight: statusLegendLineHeight,
              whiteSpace: 'nowrap',
            }}
          >
            {`(${percentage}%)`}
          </Text>
        ) : null}
      </Box>
    );
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        boxSizing: 'border-box',
        display: 'grid',
        justifyContent: 'center',
        justifyItems: 'stretch',
        maxWidth: '100%',
        mx: 'auto',
        overflow: 'hidden',
        position: 'relative',
        rowGap: s('xs'),
        width: `min(100%, 332px)`,
      }}
    >
      <Box
        ref={measureRef}
        aria-hidden="true"
        sx={{
          display: 'grid',
          rowGap: s('xs'),
          visibility: 'hidden',
          pointerEvents: 'none',
          position: 'absolute',
          inset: '0 auto auto 0',
          width: '100%',
        }}
      >
        {entries.map((entry, index) => renderRow(entry, index, true, true))}
      </Box>
      {entries.map((entry, index) => renderRow(entry, index, !hidePercentages))}
    </Box>
  );
};
